import { endOfExpiryDay, utcDayOf } from '../util/batch-expiry.js';
import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { DiscardBatchReason, DiscardReturnReason, PatientTherapySequenceStatus, RestVialStatus, ReturnReason, ReturnStatus, StabilityStage, StockMovementSource, StockMovementType, } from '../database/schema.js';
import { ENV } from '../env.js';
import { buildAmmsRestVialDiscardMessage, computeAmmsPackageContent, formatAmmsDate, formatAmmsPznOrEan, getAmmsRestVialSkipReason, } from '../hl7/message.js';
import { countPriorAmmsLogs, logAmmsOutboundMessage, nextAmmsControlSequence } from './utils/amms.js';
import { lockRestVials } from './utils/rest-vials.js';
import { assertBatchEditable, BATCH_IN_USE_ERROR, getDiscardQuantity, getLockedBatchFieldChanges, isBatchUsed, } from './utils/batch-usage.js';
import { queries } from '../queries.js';
import { Status, zql } from '../schema.js';
import { buildProductName } from '../util/patients-therapies.js';
import { collectTradeNameStabilityRules, getEarliestStabilityRuleExpiry } from '../util/stability-expiry.js';
import { assertCan, assertIsAuthenticated, EventLogs, isDefined } from '../utils.js';
import { addEventLog, DeleteResourceSchema, EventLogResources } from './util.js';
import { findProducedAt, getReturnExpiry, getReturnExpirySources, isReturnExpired } from './utils/return-expiry.js';
import { changedItems, itemsDetail, valueItem } from '../util/event-log-items.js';
import { recordStockMovement } from './utils/stock-movements.js';
export const AddBatchSchema = z.object({
    tradeNameId: z.uuid().nullable(),
    consumableId: z.uuid().nullable(),
    supplierCompanyId: z.uuid().optional(),
    lotNumber: z.string().nonoptional(),
    expiryDate: z.number().nonoptional(),
    receivedQuantity: z.number().nonoptional(),
    quantity: z.number().nonoptional(),
    status: Status.nonoptional(),
    stockLocationId: z.uuid().nonoptional(),
    note: z.string().optional(),
});
export const UpdateBatchSchema = AddBatchSchema.partial().extend({
    id: z.uuid().nonempty(),
});
// Add these schemas near the top with the other schemas
export const AddRestVialSchema = z.object({
    sequenceId: z.uuid().optional(),
    tradeNameId: z.uuid().optional(),
    amount: z.number().optional(),
    stockLocationId: z.uuid().optional(),
    expiryDate: z.number().optional(),
    batchId: z.uuid().optional(),
});
export const AddProductionRestVialsSchema = z.object({
    sequenceId: z.uuid(),
    restVials: z.array(z.object({ batchId: z.uuid(), amount: z.number() })).nonempty(),
});
export const UpdateRestVialsLocationSchema = z.object({
    restVialIds: z.array(z.uuid()).nonempty(),
    stockLocationId: z.uuid().nonempty(),
});
export const UpdateReturnLocationSchema = z.object({
    returnIds: z.array(z.uuid()).nonempty(),
    stockLocationId: z.uuid().nonempty(),
});
export const AddReturnSchema = z.object({
    medicationName: z.string().optional(),
    medicationForm: z.string().optional(),
    carrierId: z.uuid().optional(),
    sequenceId: z.uuid().optional(),
    stockLocationId: z.uuid().nonempty(),
    expiryDate: z.number().optional(),
    reason: ReturnReason.optional(),
    note: z.string().optional(),
});
const DiscardItemSchema = z.object({
    id: z.uuid(),
    stockLocationId: z.uuid().nullable(),
    medication: z.string().optional(),
});
const BaseDiscardSchema = z.object({
    items: z.array(DiscardItemSchema).nonempty(),
    reason: z.string(),
    note: z.string().optional(),
});
const AddDiscardsSchema = BaseDiscardSchema.extend({
    type: z.enum(['restVial', 'return']),
});
export const DiscardBatchSchema = z.object({
    id: z.uuid(),
    reason: DiscardBatchReason,
    note: z.string().optional(),
});
export const AcknowledgeExpiredBatchSchema = z.object({
    batchId: z.uuid(),
});
async function findById(tx, id) {
    return await tx.run(zql.batches.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
async function findByIdWithStabilityRules(tx, id) {
    return await tx.run(zql.batches
        .where('id', '=', id)
        .where('deletedAt', 'IS', null)
        .related('tradeName', tn => tn
        .related('medicationProfile', profile => profile.related('stabilityRules', sr => sr.related('stabilityRule')))
        .related('stabilityRules', sr => sr.related('stabilityRule')))
        .one());
}
function getRestVialStabilityStage(batch) {
    if (!batch.tradeName)
        return undefined;
    return batch.tradeName.medicationProfile?.isDrySubstance
        ? StabilityStage.enum.AFTER_RECONSTITUTION
        : StabilityStage.enum.AFTER_OPENING;
}
function resolveRestVialExpiryDate(batch, referenceTime) {
    const stage = getRestVialStabilityStage(batch);
    const ruleExpiry = stage && batch.tradeName
        ? getEarliestStabilityRuleExpiry(collectTradeNameStabilityRules(batch.tradeName), stage, referenceTime)
        : undefined;
    // the vial is usable through its expiry day (CY-371)
    const batchExpiry = endOfExpiryDay(batch.expiryDate);
    if (ruleExpiry === undefined)
        return batchExpiry;
    return Math.min(ruleExpiry, batchExpiry);
}
export async function createRestVialFromBatch(tx, args, ctx) {
    assertIsAuthenticated(ctx);
    const now = Date.now();
    const userActions = {
        createdAt: now,
        createdBy: ctx.user.id,
    };
    let { sequenceId, tradeNameId, amount, stockLocationId, expiryDate, batchId } = args;
    // Resolve fields from the source batch when not explicitly provided
    if (!stockLocationId || !tradeNameId || !expiryDate) {
        const batch = batchId ? await findByIdWithStabilityRules(tx, batchId) : null;
        if (batch) {
            stockLocationId ??= batch.stockLocationId ?? undefined;
            tradeNameId ??= batch.tradeNameId ?? undefined;
            expiryDate ??= resolveRestVialExpiryDate(batch, now);
        }
    }
    const internalId = await getRestVialInternalId(tx);
    const newRestVial = {
        internalId,
        sequenceId,
        tradeNameId,
        amount,
        stockLocationId,
        expiryDate,
        batchId,
        status: RestVialStatus.enum.AVAILABLE,
        ...userActions,
        id: uuidv7(),
    };
    await tx.mutate.restVials.insert(newRestVial);
    // the production left a rest vial, for the Reports filter (CY-353)
    if (sequenceId) {
        await tx.mutate.patientTherapySequence.update({
            id: sequenceId,
            restVialCreated: true,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
    }
    // a rest vial amount is in the trade name's strength unit (mg), not in packages
    const restVialUnitId = tradeNameId ? ((await getTradeName(tx, tradeNameId))?.strengthUnitId ?? null) : null;
    await recordStockMovement(tx, ctx, {
        type: StockMovementType.enum.rest_vial_created,
        batchId: batchId ?? null,
        quantity: amount ?? null,
        quantityUnitId: restVialUnitId,
        source: StockMovementSource.enum.rest_vial,
        stockLocationId: stockLocationId ?? null,
        sequenceId: sequenceId ?? null,
        restVialId: newRestVial.id,
    }, now);
    if (tx.location === 'server') {
        const patches = createPatch({}, newRestVial);
        const tradeName = await getTradeName(tx, tradeNameId || '');
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.restVial,
            resourceId: newRestVial.id,
            action: 'create',
            event: EventLogs.REST_VIAL_CREATED,
            patches,
            performedBy: ctx.user.id,
            performedAt: now,
            domain: 'batch',
            status: 'OK',
            details: {
                id: newRestVial.id,
                name: tradeName?.name,
                itemName: tradeName?.name,
                ...itemsDetail([
                    ...valueItem('quantity', amount, { unit: await getRestVialUnit(tx, tradeNameId) }),
                    ...valueItem('expiry', expiryDate, { type: 'datetime' }),
                ]),
            },
        });
    }
    return newRestVial;
}
async function getTradeName(tx, id) {
    return await tx.run(zql.tradeNames.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
/** Unit of a rest vial amount in logs: the trade name's strength unit, or mg as the stock list shows it. */
async function getRestVialUnit(tx, tradeNameId) {
    if (!tradeNameId)
        return 'mg';
    const tradeName = await tx.run(zql.tradeNames.where('id', '=', tradeNameId).related('strengthUnit').one());
    return tradeName?.strengthUnit?.ucumCode || 'mg';
}
async function getConsumable(tx, id) {
    return await tx.run(zql.consumables.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
async function getLocation(tx, id) {
    return await tx.run(zql.stockLocations.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
async function getResourceDetails(tx, consumableId, tradeNameId) {
    const tradeName = tradeNameId ? await getTradeName(tx, tradeNameId) : null;
    const consumable = consumableId ? await getConsumable(tx, consumableId) : null;
    return {
        itemName: tradeName?.name || consumable?.name,
        itemId: tradeName?.id || consumable?.id,
    };
}
/** A location change by name, or nothing when the location did not change. */
async function locationChangeItems(tx, oldId, newId) {
    if (newId === undefined || (oldId ?? null) === (newId ?? null))
        return [];
    const oldLocation = oldId ? await getLocation(tx, oldId) : null;
    const newLocation = newId ? await getLocation(tx, newId) : null;
    return [{ field: 'location', old: oldLocation?.name ?? null, new: newLocation?.name ?? null }];
}
/** Name, lot and remaining quantity of a batch, rest vial (in its strength unit) or return, for stock logs. */
async function getStockItemDetails(tx, type, id) {
    if (type === 'batch') {
        const batch = await tx.run(zql.batches.where('id', '=', id).one());
        const { itemName } = await getResourceDetails(tx, batch?.consumableId, batch?.tradeNameId);
        return { itemName, lotNumber: batch?.lotNumber, quantity: batch?.quantity, unit: undefined };
    }
    if (type === 'restVial') {
        const restVial = await tx.run(zql.restVials.where('id', '=', id).one());
        const tradeName = restVial?.tradeNameId ? await getTradeName(tx, restVial.tradeNameId) : null;
        const batch = restVial?.batchId ? await tx.run(zql.batches.where('id', '=', restVial.batchId).one()) : null;
        const unit = await getRestVialUnit(tx, restVial?.tradeNameId);
        return { itemName: tradeName?.name, lotNumber: batch?.lotNumber, quantity: restVial?.amount, unit };
    }
    const ret = await tx.run(zql.returns.where('id', '=', id).one());
    return { itemName: ret?.medicationName ?? undefined, lotNumber: undefined, quantity: undefined, unit: undefined };
}
async function deleteRestVials(tx, restVialIds, ctx) {
    assertIsAuthenticated(ctx);
    const now = Date.now();
    const userActions = {
        deletedAt: now,
        deletedBy: ctx.user.id,
    };
    await Promise.all(restVialIds.map(async (id) => {
        const updatedRestVial = {
            id,
            ...userActions,
        };
        await tx.mutate.restVials.update(updatedRestVial);
        if (tx.location === 'server') {
            const patches = createPatch({}, updatedRestVial);
            const item = await getStockItemDetails(tx, 'restVial', id);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.restVial,
                resourceId: id,
                action: 'delete',
                event: EventLogs.REST_VIAL_DISCARDED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'WARNING',
                details: {
                    itemName: item.itemName,
                    ...itemsDetail(valueItem('quantity', item.quantity, item.unit ? { unit: item.unit } : {})),
                },
            }, ctx);
        }
    }));
}
async function deleteReturns(tx, returnIds, ctx) {
    assertIsAuthenticated(ctx);
    const now = Date.now();
    const userActions = {
        deletedAt: now,
        deletedBy: ctx.user.id,
    };
    await Promise.all(returnIds.map(async (id) => {
        const updatedReturn = {
            id,
            ...userActions,
        };
        await tx.mutate.returns.update(updatedReturn);
        if (tx.location === 'server') {
            const patches = createPatch({}, updatedReturn);
            const item = await getStockItemDetails(tx, 'return', id);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.return,
                resourceId: id,
                action: 'delete',
                event: EventLogs.RETURN_DISCARDED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'WARNING',
                details: { medicationName: item.itemName },
            }, ctx);
        }
    }));
}
async function addDiscards(tx, args, ctx) {
    assertIsAuthenticated(ctx);
    await Promise.all(args.items.map(item => createDiscard(tx, { type: args.type, id: item.id, stockLocationId: item.stockLocationId }, args.reason, ctx, args.note)));
}
export async function createDiscard(tx, entity, reason, ctx, note) {
    assertIsAuthenticated(ctx);
    const now = Date.now();
    const discardId = uuidv7();
    await tx.mutate.discards.insert({
        id: discardId,
        batchId: entity.type === 'batch' ? entity.id : null,
        restVialId: entity.type === 'restVial' ? entity.id : null,
        returnId: entity.type === 'return' ? entity.id : null,
        lastLocationId: entity.stockLocationId,
        reason,
        note: note ?? null,
        createdAt: now,
        createdBy: ctx.user.id,
    });
    await recordStockMovement(tx, ctx, {
        type: StockMovementType.enum.discarded,
        batchId: entity.type === 'batch' ? entity.id : null,
        restVialId: entity.type === 'restVial' ? entity.id : null,
        returnId: entity.type === 'return' ? entity.id : null,
        quantity: entity.quantity ?? null,
        stockLocationId: entity.stockLocationId,
        discardId,
        reason: reason,
        note: note ?? null,
    }, now);
    if (tx.location === 'server') {
        const item = await getStockItemDetails(tx, entity.type, entity.id);
        // a batch is already at 0 here, so its discarded amount comes from the movement
        const discardedQuantity = entity.quantity === undefined ? item.quantity : Math.abs(entity.quantity);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.discard,
            resourceId: discardId,
            action: 'create',
            event: EventLogs.STOCK_DISCARDED,
            performedBy: ctx.user.id,
            performedAt: now,
            domain: 'batch',
            status: 'OK',
            details: {
                [`${entity.type}Id`]: entity.id,
                reason,
                itemName: item.itemName,
                lotNumber: item.lotNumber,
                ...itemsDetail([
                    ...valueItem('quantity', discardedQuantity, item.unit ? { unit: item.unit } : {}),
                    ...valueItem('reason', reason, { valueKey: 'discard.{value}' }),
                ]),
            },
        }, ctx);
    }
    return discardId;
}
/**
 * Builds and logs the outbound AMMS ZRM^O01 message (MSH/ORC/NTE/RQD/ZIV) for a rest vial
 * disposal ("Extraordinary Losses" interface, per section 10 of the AMMS integration spec), for
 * every way a rest vial stops being usable: manual discard, spoiled or scrapped production (the
 * expiry job builds the same message server-side). A rest vial whose batch lacks AMMS
 * traceability is logged as SKIPPED with the reason, never sent (spec §10.6/§10.7); one with
 * nothing left is not logged at all.
 * Server-only: HL7EventLog rows are written directly, not synced from the client.
 */
export async function logAmmsRestVialDiscardMessage(tx, params) {
    if (!ENV.AMMS_SYSTEM || tx.location !== 'server')
        return;
    const { restVialId, now } = params;
    const restVial = await tx.run(zql.restVials
        .where('id', '=', restVialId)
        .related('batch', b => b.related('tradeName'))
        .related('stockLocation')
        .related('tradeName', tn => tn.related('strengthUnit'))
        .one());
    if (!restVial)
        throw new Error(`Rest vial not found: ${restVialId}`);
    // Nothing left to write off (spec §10.3: remaining quantity > 0).
    if (!(Number(restVial.amount ?? 0) > 0))
        return;
    const batch = restVial.batch;
    const tradeName = restVial.tradeName;
    const logCount = await countPriorAmmsLogs(tx, restVial.id);
    await logAmmsOutboundMessage(tx, {
        now,
        resourceRef: [{ resource: EventLogResources.restVial, id: restVial.id }],
        skipReason: getAmmsRestVialSkipReason(batch),
        build: async () => buildAmmsRestVialDiscardMessage({
            controlSequence: await nextAmmsControlSequence(tx),
            discardedAt: new Date(now),
            restVialNumber: restVial.restVialNumber ?? 0,
            stockLocationCode: restVial.stockLocation?.code ?? '',
            logCount,
            remainingAmount: Number(restVial.amount ?? 0),
            unit: tradeName?.strengthUnit?.displayName ?? 'mg',
            packageQuantity: computeAmmsPackageContent(tradeName) ?? 0,
            product: batch
                ? {
                    ammsProductId: batch.ammsProductId,
                    description: batch.ammsProductDescription ?? batch.tradeName?.name ?? '',
                    manufacturerBatch: batch.lotNumber,
                    expiryDate: formatAmmsDate(new Date(batch.expiryDate)),
                }
                : undefined,
            batch: batch
                ? { rawZiv: batch.rawZiv, manufacturerBatch: batch.lotNumber, pznOrEan: formatAmmsPznOrEan(batch.tradeName) }
                : undefined,
        }),
    });
}
async function getRestVialInternalId(tx) {
    await lockRestVials(tx);
    // expired vials stay on the shelf with their label until discarded, so their number stays taken
    const active = await tx.run(zql.restVials
        .where('deletedAt', 'IS', null)
        .where('status', 'IN', [RestVialStatus.enum.AVAILABLE, RestVialStatus.enum.IN_USE]));
    const taken = new Set(active.map(v => v.internalId).filter(Boolean));
    let id = 1;
    while (taken.has(id))
        id++;
    return id;
}
function buildReturnMedicationForm(sequence) {
    return [
        sequence.applicationForm?.name,
        sequence.carrierSolution?.name,
        sequence.volume ? `${sequence.volume} ml` : '',
    ]
        .filter(Boolean)
        .join(' - ');
}
// The return keeps the preparation's own use-by (as on the label), counted from production (CY-328).
async function resolveReturnExpiryDate(tx, sequence, returnedAt) {
    const statusLogs = await tx.run(zql.eventLogs
        .where('resource', '=', EventLogResources.patientTherapySequence)
        .where('resourceId', '=', sequence.id)
        .where('event', '=', EventLogs.PRESCRIPTION_STATUS_CHANGE)
        .orderBy('performedAt', 'desc'));
    const producedAt = findProducedAt(statusLogs);
    // the lots this prescription used, not every batch of the picking list (CY-382)
    const orderBatchIds = sequence.batches?.map(link => link.batchId) ?? [];
    const usedBatches = orderBatchIds.length
        ? await tx.run(zql.batches.where('id', 'IN', orderBatchIds).where('deletedAt', 'IS', null))
        : [];
    const sources = getReturnExpirySources(sequence);
    const drugBatchIds = new Set(sources.drugBatchIds);
    const carrierBatchIds = new Set(sources.carrierBatchIds);
    const drugBatches = usedBatches.filter(batch => drugBatchIds.has(batch.id));
    const carrierBatches = usedBatches.filter(batch => carrierBatchIds.has(batch.id));
    const drugTradeNames = await findTradeNamesByIds(tx, drugBatches.map(batch => batch.tradeNameId).filter(isDefined));
    const carrierTradeNames = await findTradeNamesByIds(tx, carrierBatches.map(batch => batch.tradeNameId).filter(isDefined));
    return getReturnExpiry({
        producedAt,
        returnedAt,
        drugRules: drugTradeNames.flatMap(collectTradeNameStabilityRules),
        carrierRules: carrierTradeNames.flatMap(collectTradeNameStabilityRules),
        drugBatchExpiries: drugBatches.map(batch => batch.expiryDate).filter(isDefined).map(endOfExpiryDay),
        carrierBatchExpiries: carrierBatches.map(batch => batch.expiryDate).filter(isDefined).map(endOfExpiryDay),
        returnExpiries: sources.returnExpiries,
        drugIsDrySubstance: drugTradeNames.some(tradeName => tradeName.medicationProfile?.isDrySubstance),
        carrierIsDrySubstance: carrierTradeNames.some(tradeName => tradeName.medicationProfile?.isDrySubstance),
    });
}
async function findTradeNamesByIds(tx, tradeNameIds) {
    if (!tradeNameIds.length)
        return [];
    return await tx.run(zql.tradeNames
        .where('id', 'IN', tradeNameIds)
        .where('deletedAt', 'IS', null)
        .related('medicationProfile', profile => profile.related('stabilityRules', sr => sr.related('stabilityRule')))
        .related('stabilityRules', sr => sr.related('stabilityRule')));
}
export async function createReturnFromSequence(tx, args, ctx) {
    assertIsAuthenticated(ctx);
    const now = Date.now();
    const medicationName = buildProductName(args.sequence);
    const medicationForm = buildReturnMedicationForm(args.sequence);
    const expiryDate = await resolveReturnExpiryDate(tx, args.sequence, now);
    // a bag returned after its use-by is discarded at once, as the expiry job would do (CY-328)
    const expired = !args.discardedAs && isReturnExpired(expiryDate, now);
    const discarded = Boolean(args.discardedAs) || expired;
    const newReturn = {
        id: uuidv7(),
        medicationName,
        medicationForm,
        carrierId: args.sequence.carrierSolutionId,
        sequenceId: args.sequence.id,
        stockLocationId: args.stockLocationId,
        expiryDate,
        reason: args.reason,
        note: args.note,
        status: discarded ? ReturnStatus.enum.DISCARDED : ReturnStatus.enum.AVAILABLE,
        createdAt: now,
        createdBy: ctx.user.id,
    };
    await tx.mutate.returns.insert(newReturn);
    // a rejected bag never came back to stock, so its only movement is the discard below
    if (!args.discardedAs) {
        await recordStockMovement(tx, ctx, {
            type: StockMovementType.enum.returned,
            sequenceId: args.sequence.id,
            stockLocationId: args.stockLocationId ?? null,
            returnId: newReturn.id,
            source: StockMovementSource.enum.new_pack,
        }, now);
    }
    if (tx.location === 'server') {
        const patches = createPatch({}, newReturn);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.return,
            resourceId: newReturn.id,
            action: 'create',
            event: EventLogs.RETURN_CREATED,
            patches,
            performedBy: ctx.user.id,
            performedAt: now,
            domain: 'batch',
            status: 'OK',
            details: {
                id: newReturn.id,
                name: `${medicationName} - ${medicationForm}`,
                medicationName,
            },
        });
    }
    if (args.discardedAs) {
        await createDiscard(tx, { type: 'return', id: newReturn.id, stockLocationId: args.stockLocationId }, args.discardedAs.reason, ctx, args.discardedAs.note);
    }
    else if (expired) {
        await createDiscard(tx, { type: 'return', id: newReturn.id, stockLocationId: args.stockLocationId ?? null }, DiscardReturnReason.enum.AUTO_EXPIRED, ctx);
    }
    return newReturn;
}
export const stocks = {
    addBatch: defineMutator(AddBatchSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const { tradeNameId, consumableId, supplierCompanyId, lotNumber, expiryDate, receivedQuantity, quantity, status, stockLocationId, note, } = args;
        const newBatch = {
            tradeNameId,
            consumableId,
            supplierCompanyId,
            lotNumber,
            // stored as the calendar day, whatever time the client sent (CY-371)
            expiryDate: utcDayOf(expiryDate),
            receivedAt: now,
            receivedQuantity,
            quantity,
            status,
            stockLocationId,
            note,
            ...userActions,
            id: uuidv7(),
        };
        await tx.mutate.batches.insert(newBatch);
        await recordStockMovement(tx, ctx, {
            type: StockMovementType.enum.received,
            batchId: newBatch.id,
            quantity: receivedQuantity,
            source: StockMovementSource.enum.new_pack,
            stockLocationId,
            note: note ?? null,
        }, now);
        if (tx.location === 'server') {
            const patches = createPatch({}, newBatch);
            const details = await getResourceDetails(tx, consumableId, tradeNameId);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.batch,
                resourceId: newBatch.id,
                action: 'create',
                event: EventLogs.BATCH_CREATED,
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'OK',
                details: {
                    ...details,
                    batchId: newBatch.lotNumber,
                    lotNumber: newBatch.lotNumber,
                    ...itemsDetail([...valueItem('quantity', quantity), ...valueItem('expiry', expiryDate, { type: 'date' })]),
                },
            }, ctx);
        }
    }),
    updateBatch: defineMutator(UpdateBatchSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not Found');
        // disposed (expired and acknowledged) and discarded batches stay as they are
        assertBatchEditable(prev);
        // product, lot and expiry of a used batch are what patients received, so they stay as recorded (CY-371)
        if (getLockedBatchFieldChanges(prev, args).length > 0 && (await isBatchUsed(tx, args.id))) {
            throw new Error(BATCH_IN_USE_ERROR);
        }
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        const { tradeNameId, consumableId, supplierCompanyId, lotNumber, expiryDate, receivedQuantity, quantity, status, stockLocationId, note, } = args;
        const updatedBatch = {
            id: args.id,
            tradeNameId,
            consumableId,
            supplierCompanyId,
            lotNumber,
            expiryDate: expiryDate === undefined ? undefined : utcDayOf(expiryDate),
            receivedQuantity,
            quantity,
            status,
            stockLocationId,
            note,
            ...userActions,
        };
        await tx.mutate.batches.update(updatedBatch);
        // a changed quantity is a correction; a changed location is a transfer (both are movements)
        const previousQuantity = Number(prev.quantity ?? 0);
        const nextQuantity = quantity === undefined ? previousQuantity : Number(quantity);
        if (nextQuantity !== previousQuantity) {
            await recordStockMovement(tx, ctx, {
                type: StockMovementType.enum.corrected,
                batchId: args.id,
                quantity: nextQuantity - previousQuantity,
                stockLocationId: stockLocationId ?? prev.stockLocationId,
                note: note ?? null,
            }, now);
        }
        if (stockLocationId !== undefined && stockLocationId !== prev.stockLocationId) {
            await recordStockMovement(tx, ctx, { type: StockMovementType.enum.transferred, batchId: args.id, stockLocationId }, now);
        }
        if (tx.location === 'server') {
            // fields left out of the update are unchanged, not removed
            const sentFields = Object.fromEntries(Object.entries(updatedBatch).filter(([, value]) => value !== undefined));
            const patches = createPatch(prev, { ...prev, ...sentFields });
            // a used batch is saved without its locked fields, so the stored ones name it in the log
            const details = await getResourceDetails(tx, consumableId ?? prev.consumableId, tradeNameId ?? prev.tradeNameId);
            const items = [
                ...changedItems(prev, sentFields, [
                    { field: 'quantity' },
                    { field: 'expiry', key: 'expiryDate', type: 'date' },
                    { field: 'lot', key: 'lotNumber' },
                ]),
                ...(await locationChangeItems(tx, prev.stockLocationId, stockLocationId)),
            ];
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'batch',
                resourceId: args.id,
                action: 'update',
                event: EventLogs.BATCH_UPDATED,
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'OK',
                details: {
                    ...details,
                    batchId: updatedBatch.lotNumber ?? prev.lotNumber,
                    lotNumber: updatedBatch.lotNumber ?? prev.lotNumber,
                    ...itemsDetail(items),
                },
            }, ctx);
        }
    }),
    deleteBatch: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        // Prevent deleting disposed batches (expired and acknowledged)
        if (prev.acknowledgedAt) {
            throw new Error('Cannot delete a disposed batch');
        }
        // a used batch is discarded instead, so its history keeps pointing at it (CY-371)
        if (await isBatchUsed(tx, args.id)) {
            throw new Error(BATCH_IN_USE_ERROR);
        }
        const now = Date.now();
        const userActions = { deletedAt: now, deletedBy: ctx.user.id };
        // the lot number is kept so the batch's history still names it (CY-45)
        const updatedBatch = {
            id: args.id,
            lotNumber: prev.lotNumber,
            ...userActions,
        };
        await tx.mutate.batches.update(updatedBatch);
        if (tx.location === 'server') {
            const details = await getResourceDetails(tx, prev.consumableId, prev.tradeNameId);
            const patches = createPatch(prev, updatedBatch);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.batch,
                resourceId: args.id,
                action: 'delete',
                event: EventLogs.BATCH_DELETED,
                patches: patches,
                performedBy: updatedBatch.deletedBy,
                performedAt: updatedBatch.deletedAt,
                domain: 'batch',
                status: 'WARNING',
                details: {
                    ...details,
                    batchId: updatedBatch.lotNumber,
                    // the stored lot gets a _deleted suffix; the log shows the lot people know
                    lotNumber: prev.lotNumber,
                },
            }, ctx);
        }
    }),
    discardBatch: defineMutator(DiscardBatchSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        assertBatchEditable(prev);
        const quantity = getDiscardQuantity(prev.quantity);
        const now = Date.now();
        // the batch stays with its lot number; only its remaining stock goes, as a discard movement
        const updatedBatch = {
            id: args.id,
            quantity: 0,
            status: Status.enum.inactive,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.batches.update(updatedBatch);
        await createDiscard(tx, { type: 'batch', id: args.id, stockLocationId: prev.stockLocationId, quantity }, args.reason, ctx, args.note);
        if (tx.location === 'server') {
            const details = await getResourceDetails(tx, prev.consumableId, prev.tradeNameId);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.batch,
                resourceId: args.id,
                action: 'update',
                patches: createPatch(prev, { ...prev, ...updatedBatch }),
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'WARNING',
                details: {
                    ...details,
                    batchId: prev.lotNumber,
                },
            }, ctx);
        }
    }),
    addRestVial: defineMutator(AddRestVialSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        await createRestVialFromBatch(tx, args, ctx);
    }),
    // Creates the leftovers of a cleanroom production. Idempotent per prescription: re-running the
    // cleanroom flow (reload, second station) must not register the same physical vials twice.
    addProductionRestVials: defineMutator(AddProductionRestVialsSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'production.produce');
        await lockRestVials(tx);
        if (tx.location === 'server') {
            const sequence = await tx.run(zql.patientTherapySequence.where('id', '=', args.sequenceId).one());
            if (sequence?.status !== PatientTherapySequenceStatus.enum.in_production) {
                throw new Error('Rest vials can only be registered while the prescription is in production.');
            }
        }
        // an active rest vial of this sequence means this production already registered its leftovers.
        // Inactive ones are ignored: before v0.3.4 an abort + remake kept the same sequence in production,
        // so it can carry used/discarded rest vials from an earlier, legitimately separate production.
        const existing = await tx.run(zql.restVials
            .where('sequenceId', '=', args.sequenceId)
            .where('deletedAt', 'IS', null)
            .where('status', 'IN', [RestVialStatus.enum.AVAILABLE, RestVialStatus.enum.IN_USE])
            .one());
        if (existing)
            return;
        // sequential so each vial sees the previous one's label number
        for (const restVial of args.restVials) {
            // oxlint-disable-next-line no-await-in-loop
            await createRestVialFromBatch(tx, { ...restVial, sequenceId: args.sequenceId }, ctx);
        }
    }),
    updateRestVialLocation: defineMutator(UpdateRestVialsLocationSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await Promise.all(args.restVialIds.map(async (id) => {
            const updatedRestVial = {
                id,
                stockLocationId: args.stockLocationId,
                ...userActions,
            };
            const prevRestVial = tx.location === 'server' ? await tx.run(zql.restVials.where('id', '=', id).one()) : undefined;
            await tx.mutate.restVials.update(updatedRestVial);
            await recordStockMovement(tx, ctx, { type: StockMovementType.enum.transferred, restVialId: id, stockLocationId: args.stockLocationId }, now);
            if (tx.location === 'server') {
                const patches = createPatch({}, updatedRestVial);
                const location = await getLocation(tx, args.stockLocationId);
                const item = await getStockItemDetails(tx, 'restVial', id);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.restVial,
                    resourceId: id,
                    action: 'update',
                    event: EventLogs.REST_VIAL_MOVED,
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {
                        id: args.stockLocationId,
                        name: location?.name,
                        itemName: item.itemName,
                        ...itemsDetail(await locationChangeItems(tx, prevRestVial?.stockLocationId, args.stockLocationId)),
                    },
                }, ctx);
            }
        }));
    }),
    consumeRestVials: defineMutator(z.array(z.uuid()), async ({ args, ctx, tx }) => {
        assertCan(ctx, 'production.produce');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        // only vials still allocated to a production; never revive a discarded one
        const inUse = await tx.run(zql.restVials.where('id', 'IN', args).where('status', '=', RestVialStatus.enum.IN_USE));
        // the amount is in the trade name's strength unit (mg), so the movement carries that unit
        const tradeNameIds = Array.from(new Set(inUse.map(vial => vial.tradeNameId).filter((id) => !!id)));
        const tradeNames = tradeNameIds.length ? await tx.run(zql.tradeNames.where('id', 'IN', tradeNameIds)) : [];
        const unitByTradeName = new Map(tradeNames.map(tradeName => [tradeName.id, tradeName.strengthUnitId]));
        await Promise.all(inUse.map(async (vial) => {
            const { id } = vial;
            await tx.mutate.restVials.update({
                id,
                status: RestVialStatus.enum.USED,
                ...userActions,
            });
            await recordStockMovement(tx, ctx, {
                type: StockMovementType.enum.rest_vial_taken,
                batchId: vial.batchId ?? null,
                quantity: vial.amount != null ? -Number(vial.amount) : null,
                quantityUnitId: (vial.tradeNameId && unitByTradeName.get(vial.tradeNameId)) || null,
                source: StockMovementSource.enum.rest_vial,
                stockLocationId: vial.stockLocationId ?? null,
                sequenceId: vial.sequenceId ?? null,
                restVialId: id,
            }, now);
            if (tx.location === 'server') {
                const tradeName = vial.tradeNameId ? await getTradeName(tx, vial.tradeNameId) : null;
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.restVial,
                    resourceId: id,
                    action: 'update',
                    event: EventLogs.REST_VIAL_USED,
                    patches: createPatch({ status: RestVialStatus.enum.IN_USE }, { status: RestVialStatus.enum.USED }),
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {
                        itemName: tradeName?.name,
                        ...itemsDetail(valueItem('quantity', vial.amount, { unit: await getRestVialUnit(tx, vial.tradeNameId) })),
                    },
                });
            }
        }));
    }),
    consumeReturns: defineMutator(z.array(z.uuid()), async ({ args, ctx, tx }) => {
        assertCan(ctx, 'production.produce');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await Promise.all(args.map(async (id) => {
            await tx.mutate.returns.update({
                id,
                status: ReturnStatus.enum.USED,
                ...userActions,
            });
            await recordStockMovement(tx, ctx, { type: StockMovementType.enum.consumed, returnId: id, source: StockMovementSource.enum.rest_vial }, now);
            if (tx.location === 'server') {
                const item = await getStockItemDetails(tx, 'return', id);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.return,
                    resourceId: id,
                    action: 'update',
                    event: EventLogs.RETURN_USED,
                    patches: createPatch({ status: ReturnStatus.enum.IN_USE }, { status: ReturnStatus.enum.USED }),
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: { medicationName: item.itemName },
                });
            }
        }));
    }),
    addReturn: defineMutator(AddReturnSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const { sequenceId, stockLocationId, reason, note } = args;
        // Sequence-based flow: derive name/form/expiry from the source therapy sequence
        if (sequenceId) {
            const sequence = await tx.run(queries.patientsTherapies.getSequenceById.fn({ args: sequenceId, ctx }));
            if (!sequence)
                throw new Error('Therapy sequence not found.');
            await createReturnFromSequence(tx, { sequence, stockLocationId, reason, note }, ctx);
            return;
        }
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const { medicationName, medicationForm, carrierId, expiryDate } = args;
        const newReturn = {
            medicationName,
            medicationForm,
            carrierId,
            sequenceId,
            stockLocationId,
            expiryDate,
            reason,
            note,
            status: ReturnStatus.enum.AVAILABLE,
            ...userActions,
            id: uuidv7(),
        };
        await tx.mutate.returns.insert(newReturn);
        await recordStockMovement(tx, ctx, {
            type: StockMovementType.enum.returned,
            sequenceId: sequenceId ?? null,
            stockLocationId: stockLocationId ?? null,
            returnId: newReturn.id,
            source: StockMovementSource.enum.new_pack,
        }, now);
        if (tx.location === 'server') {
            const patches = createPatch({}, newReturn);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.return,
                resourceId: newReturn.id,
                action: 'create',
                event: EventLogs.RETURN_CREATED,
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                domain: 'batch',
                status: 'OK',
                details: {
                    id: newReturn.id,
                    name: `${medicationName} - ${medicationForm}`,
                    medicationName,
                },
            });
        }
    }),
    updateReturnLocation: defineMutator(UpdateReturnLocationSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await Promise.all(args.returnIds.map(async (id) => {
            const updatedReturn = {
                id,
                stockLocationId: args.stockLocationId,
                ...userActions,
            };
            const prevReturn = tx.location === 'server' ? await tx.run(zql.returns.where('id', '=', id).one()) : undefined;
            await tx.mutate.returns.update(updatedReturn);
            await recordStockMovement(tx, ctx, { type: StockMovementType.enum.transferred, returnId: id, stockLocationId: args.stockLocationId }, now);
            if (tx.location === 'server') {
                const patches = createPatch({}, updatedReturn);
                const location = await getLocation(tx, args.stockLocationId);
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.return,
                    resourceId: id,
                    action: 'update',
                    event: EventLogs.RETURN_MOVED,
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    domain: 'batch',
                    status: 'OK',
                    details: {
                        id: args.stockLocationId,
                        name: location?.name,
                        medicationName: prevReturn?.medicationName ?? undefined,
                        ...itemsDetail(await locationChangeItems(tx, prevReturn?.stockLocationId, args.stockLocationId)),
                    },
                }, ctx);
            }
        }));
    }),
    discardRestVials: defineMutator(BaseDiscardSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const restVialIds = args.items.map(restVial => restVial.id);
        if (tx.location === 'server') {
            const now = Date.now();
            await Promise.all(restVialIds.map(restVialId => logAmmsRestVialDiscardMessage(tx, { restVialId, now })));
        }
        await deleteRestVials(tx, restVialIds, ctx);
        await addDiscards(tx, {
            items: args.items,
            reason: args.reason,
            note: args.note,
            type: 'restVial',
        }, ctx);
    }),
    discardReturns: defineMutator(BaseDiscardSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const returnIds = args.items.map(returnItem => returnItem.id);
        await deleteReturns(tx, returnIds, ctx);
        await addDiscards(tx, {
            items: args.items,
            reason: args.reason,
            note: args.note,
            type: 'return',
        }, ctx);
    }),
    acknowledgeExpiredBatch: defineMutator(AcknowledgeExpiredBatchSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'stock.manage');
        const batch = await tx.run(zql.batches.where('id', '=', args.batchId).where('deletedAt', 'IS', null).one());
        if (!batch)
            throw new Error('Batch not found');
        const timestamp = Date.now();
        const userId = ctx.user.id;
        await tx.mutate.batches.update({
            id: args.batchId,
            acknowledgedAt: timestamp,
            updatedAt: timestamp,
            updatedBy: userId,
        });
        if (tx.location === 'server') {
            const patches = createPatch({ acknowledgedAt: null }, { acknowledgedAt: new Date(timestamp).toISOString() });
            const { itemName } = await getResourceDetails(tx, batch.consumableId, batch.tradeNameId);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.batch,
                resourceId: args.batchId,
                action: 'update',
                event: EventLogs.BATCH_EXPIRY_ACKNOWLEDGED,
                domain: 'batch',
                status: 'OK',
                patches,
                performedBy: userId,
                performedAt: timestamp,
                details: {
                    itemName,
                    lotNumber: batch.lotNumber,
                    ...itemsDetail(valueItem('expiry', batch.expiryDate, { type: 'date' })),
                },
            }, ctx);
        }
    }),
};
//# sourceMappingURL=stocks.mutators.js.map